import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { formatSlotDuration } from '@heraion/shared';
import { IconButton } from '../index';
import { MEASURE_WIDTH, useTextFit } from '../primitives/useTextFit';

import type { useTheme } from './theme';
import type { TimerStartHandlers } from './timerStartHandlers';

const SLOT_BORDER = 1;

// The action column of a duration set (MX-5 slice 2) — what lives in the effort
// meter's fixed right slot, where a reps set keeps its Fail button. It does one
// job at a time, and the job is the set's state:
//
//   pending  → the start chip. One chip, its direction already decided (D6).
//   logged   → the fact: the held time, with the word under it.
//   failed   → the same fact, in the ramp's hardest tone, marked ✕.
//
// There is no Fail BUTTON here on purpose (D2): going to failure on a plank IS
// the hardest stop of the band, and the `fail` flag exists to keep a set out of
// the e1RM, which a hold has none of. Two roads to one datum would be one too
// many. But a set that already CARRIES the flag — imported, logged before this
// design, or set elsewhere — must still read as failed here: this panel is the
// household's single data-entry surface, and the timeline row right below it
// shows a Failed badge for the same set. A state that exists and can't be seen
// is worse than a state you can't set (a11y audit, 2026-07-22).
//
export function EffortTimerSlot({
  timer,
  logged,
  failed,
  heldSec,
  readsDone,
  theme,
}: {
  /** Start affordance; undefined when this entry has no timer to offer. */
  timer?: TimerStartHandlers;
  /** The bound set is already logged — the slot reports instead of inviting. */
  logged: boolean;
  /** The set carries the fail flag: attempted, not completed. */
  failed: boolean;
  /** Seconds recorded on the set (0 = nothing logged yet). */
  heldSec: number;
  /** Which word names the result. A hold reads "held" — you kept it as long
   *  as you could, and the seconds ARE the achievement. Cardio reads "done",
   *  and so does a bout the plan bounded (HI-1): both ended on a number
   *  somebody else set, so "held" would credit the athlete with an endurance
   *  they were never asked for. Same shape, honest word. */
  readsDone: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // #416: the column is a fixed 48pt (MeterRow), and the failed word (Versagen,
  // Отказ) outgrows it at large text sizes. Never truncate: the word shows only
  // when its natural width, measured on an invisible twin, fits the slot's
  // inner width; otherwise the caption keeps the ✕ alone (the sign that never
  // depended on colour; the a11y label stays complete). Both numbers come from
  // onLayout, so RN and RNW agree and nothing depends on the platform.
  const fit = useTextFit(SLOT_BORDER);

  // While a timer runs this column renders NOTHING and the meter drops it, so
  // the band takes the whole row (EffortMeter's trailingHidden). It used to
  // hold the word "Effort" in a dashed box; Antenore 2026-07-22: «quel bottone
  // grigiato con dentro scritto effort non è bello, mi convinceva davvero
  // poco» — it was put there to say what the tap does, and the caption on the
  // band (TM-10) already says it, in the place you actually touch. Removing it
  // also removes the locale problem it carried (Belastung in 48pt) and hands
  // the band the width that keeps its segments usable on a small screen.
  if (logged && heldSec > 0) {
    // A failed set takes the effort ramp's HARDEST tone — the same treatment as
    // the reps set's Fail box: you gave everything, the extreme of effort, never
    // alarm red (Antenore 2026-07-20). The ✕ carries it by shape too, so it
    // never reads by colour alone.
    const hardStop = theme.effortRamp[theme.effortRamp.length - 1];
    const failedText = `✕ ${t('mobile.logger.effortMeterFail', { defaultValue: 'Fail' })}`;
    const word = readsDone
      ? t('mobile.logger.holdSlotDone', { defaultValue: 'DONE' })
      : t('mobile.logger.holdSlotHeld', { defaultValue: 'HELD' });
    return (
      <View
        style={[
          styles.result,
          failed &&
            hardStop && {
              backgroundColor: hardStop.fill,
              borderColor: hardStop.on,
            },
        ]}
        accessible
        onLayout={fit.onBoxLayout}
        accessibilityLabel={
          failed
            ? t('mobile.logger.holdSlotFailedA11y', {
                time: formatSlotDuration(heldSec),
                defaultValue: '{{time}}, failed',
              })
            : readsDone
              ? t('mobile.logger.holdSlotDoneA11y', {
                  time: formatSlotDuration(heldSec),
                  defaultValue: '{{time}} done',
                })
              : t('mobile.logger.holdSlotHeldA11y', {
                  time: formatSlotDuration(heldSec),
                  defaultValue: '{{time}} held',
                })
        }
      >
        <Text
          style={[
            styles.resultTime,
            failed && hardStop && { color: hardStop.on },
          ]}
        >
          {formatSlotDuration(heldSec)}
        </Text>
        <Text
          style={[
            styles.resultCaption,
            failed && hardStop && { color: hardStop.on },
          ]}
          numberOfLines={1}
        >
          {failed ? (fit.fits ? failedText : '✕') : word}
        </Text>
        {failed ? (
          <View
            style={styles.measure}
            pointerEvents="none"
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            <Text
              style={[styles.resultCaption, styles.measureText]}
              numberOfLines={1}
              onLayout={fit.onTextLayout}
            >
              {failedText}
            </Text>
          </View>
        ) : null}
      </View>
    );
  }

  if (!timer) return null;

  // The glyphs carry OPPOSITE silhouettes — an hourglass's pinched waist vs a
  // stopwatch's round body — so the direction stays readable under blur for a
  // myopic reader, which is what let the old ↓/↑ captions go (TM-7). The spoken
  // label says it outright either way.
  return (
    <IconButton
      theme={theme}
      stretch
      // Deliberately the NEUTRAL variant, though the mockup tints it brand.
      // `active` would announce "selected" forever on a one-shot action
      // (name/role/value), and `primary` paints inkOnBrand on brand600 — the
      // dual-role token of DS-21, which is 2.18:1 in rosepine-light. DS-21
      // describes that as the PRESSED state erasing a label for an instant;
      // here the chip would sit in it permanently, so a Rosé Pine athlete would
      // get an invisible glyph on the control that starts the set. The a11y
      // floor beats the tint: the chip is prominent by size (it owns the whole
      // action column) and by the pulsing "Tap to log" beside it. Restore the brand
      // tint the moment DS-21 splits the token.
      variant="default"
      name={timer.mode === 'down' ? 'hourglass' : 'stopwatch'}
      onPress={timer.onStart}
      accessibilityLabel={
        timer.mode === 'down'
          ? t('mobile.logger.timerStartCountdown', {
              defaultValue: 'Start countdown',
            })
          : t('mobile.logger.timerStartCountup', {
              defaultValue: 'Start stopwatch',
            })
      }
    />
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Reads as a FACT, not a button: surface2 + plain border, no brand tint and
    // nothing tappable-looking. The editable copies of both numbers stay in the
    // duration fields above and in the band itself.
    result: {
      flex: 1,
      minHeight: 44,
      borderRadius: r.r2,
      borderWidth: SLOT_BORDER,
      borderColor: p.border,
      backgroundColor: p.surface2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    resultTime: {
      color: p.ink,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    // The twin that measures the word: out of the flow, as wide as it wants,
    // seen by no one.
    measure: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: MEASURE_WIDTH,
      opacity: 0,
      alignItems: 'flex-start',
    },
    measureText: { flexShrink: 0 },
    // xs, quiet: the number is the point, the word only says which number.
    resultCaption: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
      letterSpacing: 0.5,
    },
  });
}
